export interface WeekRange {
  startInclusive: string
  endExclusive: string
}

interface CalendarDate {
  year: number
  month: number
  day: number
}

const IANA_TIME_ZONE_ERROR = '时区必须是有效的 IANA 时区'
const ISO_INSTANT_ERROR = '时间必须是有效的 ISO 8601 时间'
const ISO_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):(\d{2}))$/u
const LOCAL_MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/u

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
    return isLeapYear ? 29 : 28
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

export function parseIsoInstant(iso: string): Date {
  const match = ISO_INSTANT_PATTERN.exec(iso)
  if (match === null) throw new Error(ISO_INSTANT_ERROR)

  const [, yearText, monthText, dayText, hourText, minuteText, secondText, fraction, zone] =
    match
  if (
    yearText === undefined ||
    monthText === undefined ||
    dayText === undefined ||
    hourText === undefined ||
    minuteText === undefined ||
    secondText === undefined ||
    zone === undefined
  ) {
    throw new Error(ISO_INSTANT_ERROR)
  }

  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const second = Number(secondText)
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    throw new Error(ISO_INSTANT_ERROR)
  }

  let offsetMinutes = 0
  if (zone !== 'Z') {
    const offsetSign = match[9]
    const offsetHourText = match[10]
    const offsetMinuteText = match[11]
    if (
      offsetSign === undefined ||
      offsetHourText === undefined ||
      offsetMinuteText === undefined
    ) {
      throw new Error(ISO_INSTANT_ERROR)
    }
    const offsetHour = Number(offsetHourText)
    const offsetMinute = Number(offsetMinuteText)
    if (offsetHour > 23 || offsetMinute > 59) throw new Error(ISO_INSTANT_ERROR)
    offsetMinutes = (offsetHour * 60 + offsetMinute) * (offsetSign === '+' ? 1 : -1)
  }

  const milliseconds = Number((fraction ?? '').padEnd(3, '0').slice(0, 3))
  const wallTime = new Date(0)
  wallTime.setUTCFullYear(year, month - 1, day)
  wallTime.setUTCHours(hour, minute, second, milliseconds)
  return new Date(wallTime.getTime() - offsetMinutes * 60_000)
}

function dateTimeParts(instant: Date, timeZone: string): Record<string, string> {
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
  } catch {
    throw new Error(IANA_TIME_ZONE_ERROR)
  }

  return Object.fromEntries(
    formatter
      .formatToParts(instant)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  )
}

function requirePart(parts: Record<string, string>, name: string): number {
  const value = parts[name]
  if (value === undefined) {
    throw new Error('无法解析本地日期')
  }
  return Number(value)
}

function calendarDateAt(instant: Date, timeZone: string): CalendarDate {
  const parts = dateTimeParts(instant, timeZone)
  return {
    year: requirePart(parts, 'year'),
    month: requirePart(parts, 'month'),
    day: requirePart(parts, 'day'),
  }
}

function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const result = new Date(Date.UTC(date.year, date.month - 1, date.day + days))
  return {
    year: result.getUTCFullYear(),
    month: result.getUTCMonth() + 1,
    day: result.getUTCDate(),
  }
}

function localMidnightUtc(date: CalendarDate, timeZone: string): Date {
  const targetWallTime = Date.UTC(date.year, date.month - 1, date.day)
  let candidate = targetWallTime

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = dateTimeParts(new Date(candidate), timeZone)
    const renderedWallTime = Date.UTC(
      requirePart(parts, 'year'),
      requirePart(parts, 'month') - 1,
      requirePart(parts, 'day'),
      requirePart(parts, 'hour'),
      requirePart(parts, 'minute'),
      requirePart(parts, 'second'),
    )
    const correction = targetWallTime - renderedWallTime
    candidate += correction
    if (correction === 0) break
  }

  return new Date(candidate)
}

export function localDayAt(iso: string, timeZone: string): string {
  const instant = parseIsoInstant(iso)
  const date = calendarDateAt(instant, timeZone)
  return `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(
    date.day,
  ).padStart(2, '0')}`
}

export function nextLocalDayStart(referenceAt: string, timeZone: string): string {
  const reference = parseIsoInstant(referenceAt)
  const nextDay = addCalendarDays(calendarDateAt(reference, timeZone), 1)
  return localMidnightUtc(nextDay, timeZone).toISOString()
}

export function durationMinutes(startAt: string, endAt: string): number {
  return (parseIsoInstant(endAt).getTime() - parseIsoInstant(startAt).getTime()) / 60_000
}

export function mondayWeekRange(referenceAt: string, timeZone: string): WeekRange {
  const reference = parseIsoInstant(referenceAt)

  const localDate = calendarDateAt(reference, timeZone)
  const weekday = new Date(Date.UTC(localDate.year, localDate.month - 1, localDate.day)).getUTCDay()
  const daysSinceMonday = (weekday + 6) % 7
  const monday = addCalendarDays(localDate, -daysSinceMonday)
  const nextMonday = addCalendarDays(monday, 7)

  return {
    startInclusive: localMidnightUtc(monday, timeZone).toISOString(),
    endExclusive: localMidnightUtc(nextMonday, timeZone).toISOString(),
  }
}

export function monthRange(monthKey: string, timeZone: string): WeekRange {
  const match = LOCAL_MONTH_PATTERN.exec(monthKey)
  if (match === null || match[1] === '0000') {
    throw new Error('月份必须使用 YYYY-MM 格式')
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const start = { year, month, day: 1 }
  const next =
    month === 12 ? { year: year + 1, month: 1, day: 1 } : { year, month: month + 1, day: 1 }

  return {
    startInclusive: localMidnightUtc(start, timeZone).toISOString(),
    endExclusive: localMidnightUtc(next, timeZone).toISOString(),
  }
}
